import React, { useState, useEffect, useMemo } from "react";
import Rencanakan from "./RancangPage";
import "./MenulisPage.css";
import { userGetItem, userSetItem } from "../utils/userStorage";
import { downloadPdf, printPdf } from "../utils/pdfActions";
import { toast } from "react-toastify";

import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

import pdfFile from "../assets/panduan_menulis.pdf";

function computeProgress() {
  const panduan = Boolean(userGetItem("rancang_panduan_viewed"));
  const naskah = Boolean(userGetItem("rancang_naskah_opened"));
  const saved = Boolean(userGetItem("rancang_story_saved"));
  let percent = 0;
  if (panduan) percent = 25;
  if (panduan && naskah) percent = 50;
  if (panduan && naskah && saved) percent = 100;
  return { panduan, naskah, saved, percent };
}

export default function MenulisPage({ onGoToDesain }) {
  const [activeTab, setActiveTab] = useState("panduan");
  const [progress, setProgress] = useState(computeProgress);

  const handleTab = (tab) => {
    setActiveTab(tab);
    if (tab === "panduan") userSetItem("rancang_panduan_viewed", "1");
    if (tab === "naskah") userSetItem("rancang_naskah_opened", "1");
    setProgress(computeProgress());
  };

  useEffect(() => {
    if (activeTab === "panduan") userSetItem("rancang_panduan_viewed", "1");
    if (activeTab === "naskah") userSetItem("rancang_naskah_opened", "1");
    setProgress(computeProgress());
  }, [activeTab]);

  useEffect(() => {
    const onStorage = () => setProgress(computeProgress());
    window.addEventListener("storage", onStorage);
    const interval = setInterval(onStorage, 2000);
    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(interval);
    };
  }, []);

  const badges = useMemo(
    () => [
      { emoji: "📖", done: progress.panduan, current: !progress.panduan },
      {
        emoji: "✍️",
        done: progress.naskah,
        current: progress.panduan && !progress.naskah,
      },
      {
        emoji: "🎯",
        done: progress.saved,
        current: progress.naskah && !progress.saved,
      },
      { emoji: "🏆", done: progress.percent === 100, current: false },
    ],
    [progress]
  );

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    defaultScale: "PageFit",
  });

  const handleDownloadPanduan = () => {
    downloadPdf(pdfFile, "panduan-menulis-kreatif.pdf");
    toast.success("Unduhan panduan dimulai");
  };

  const handlePrintPanduan = () => {
    if (printPdf(pdfFile)) toast.info("Buka tab baru untuk mencetak PDF");
    else toast.error("Tidak dapat membuka PDF untuk dicetak");
  };

  return (
    <div className="menulis-container">
      <div className="menulis-header">
        <div className="header-content">
          <h1>Dunia Menulis Kreatif </h1>
          <p>Jelajahi panduan menulis dan buat cerita mu sendiri!</p>
        </div>
      </div>

      <div className="tab-container">
        <div className="tab-buttons">
          <button
            className={`tab-button ${activeTab === "panduan" ? "active" : ""}`}
            onClick={() => handleTab("panduan")}
          >
            <div className="tab-icon">📖</div>
            <div className="tab-content">
              <div className="tab-title">Panduan Menulis</div>
              <div className="tab-subtitle">Pelajari caranya!</div>
            </div>
            <div className="tab-arrow">➡️</div>
          </button>

          <button
            className={`tab-button ${activeTab === "naskah" ? "active" : ""}`}
            onClick={() => handleTab("naskah")}
          >
            <div className="tab-icon">✍️</div>
            <div className="tab-content">
              <div className="tab-title">Menulis Naskah</div>
              <div className="tab-subtitle">Buat ceritamu!</div>
            </div>
            <div className="tab-arrow">➡️</div>
          </button>
        </div>

        <div className="tab-indicator">
          <div className={`indicator ${activeTab === "panduan" ? "left" : "right"}`}></div>
        </div>
      </div>

      <div className="progress-section">
        <div className="progress-info">
          <span>Progress Belajarmu</span>
          <span>{progress.percent}%</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress.percent}%` }}></div>
        </div>
        <div className="progress-badges">
          {badges.map((b, i) => (
            <div
              key={i}
              className={`badge ${b.done ? "completed" : ""} ${b.current ? "current" : ""}`}
            >
              {b.emoji}
            </div>
          ))}
        </div>
      </div>

      {activeTab === "panduan" && (
        <div className="tab-content-area">
          <div className="content-header">
            <h2>📚 Panduan Menulis Seru!</h2>
            <p>Pelajari langkah-langkah menulis cerita yang menarik</p>
          </div>

          <div className="pdf-container">
            <div className="pdf-header">
              <div className="pdf-info">
                <span className="pdf-title">Buku Panduan Menulis Kreatif</span>
                <span className="pdf-size">📄 15 halaman</span>
              </div>
              <div className="pdf-actions">
                <button type="button" className="action-btn download" onClick={handleDownloadPanduan}>
                  📥 Unduh
                </button>
                <button type="button" className="action-btn print" onClick={handlePrintPanduan}>
                  🖨️ Print
                </button>
              </div>
            </div>

            <div className="pdf-viewer-wrapper">
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <Viewer fileUrl={pdfFile} plugins={[defaultLayoutPluginInstance]} />
              </Worker>
            </div>
          </div>

          <div className="quick-tips">
            <h3>💡 Tips Cepat Menulis</h3>
            <div className="tips-grid">
              <div className="tip-card">
                <div className="tip-emoji">🎨</div>
                <h4>Cari Informasi</h4>
                <p>Bayangkan cerita seperti film di kepalamu!</p>
              </div>
              <div className="tip-card">
                <div className="tip-emoji">📝</div>
                <h4>Buat Kerangka Cerita</h4>
                <p>Jangan takut salah, tulis saja dulu!</p>
              </div>
              <div className="tip-card">
                <div className="tip-emoji">🔍</div>
                <h4>Tulis Ceritamu</h4>
                <p>Periksa kembali tulisanmu dengan teliti</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "naskah" && (
        <div className="tab-content-area">
          <div className="content-header">
            <h2>🎭 Waktu Menulis Cerita!</h2>
            <p>Kreasikan cerita mu sendiri dengan alat yang menyenangkan</p>
          </div>
          <div className="naskah-container">
            <Rencanakan onStorySaved={() => setProgress(computeProgress())} />
          </div>
        </div>
      )}

      <div className="writing-footer">
        <div className="motivation-text">
          <h3>Setiap Penulis Hebat Pernah Mulai Seperti Kamu! 🌟</h3>
          <p>Teruslah menulis dan berimajinasi!</p>
        </div>
        <div className="footer-decoration">
          <div className="bouncing-emoji">✨</div>
          <div className="bouncing-emoji">🎨</div>
          <div className="bouncing-emoji">📚</div>
        </div>
      </div>

      {onGoToDesain && (progress.saved || progress.percent >= 50) && (
        <div className="menulis-sticky-cta">
          <div className="menulis-sticky-text">
            <strong>Siap lanjut ke tahap berikutnya?</strong>
            <span>Buat visual cerita di Ruang Desain.</span>
          </div>
          <button type="button" className="btn-primary menulis-sticky-btn" onClick={onGoToDesain}>
            Lanjut ke Desain →
          </button>
        </div>
      )}
    </div>
  );
}
